import type { TrendPoint } from '../../../components/design-system/analytics';
import type { VisibilityCheck, VisibilityHistoryPoint, VisibilityRow } from '../../../common/hooks/use-ai-channels-api.hook';

/**
 * Pure readings of an AI-visibility check for the /business/ai page — kept
 * here so they are tested without rendering anything.
 */

export type AiChannelsTab = 'visibility' | 'assistants';
export const AI_CHANNELS_TABS: AiChannelsTab[] = ['visibility', 'assistants'];

export const tabFrom = (value: string | null): AiChannelsTab => (value === 'assistants' ? 'assistants' : 'visibility');

/** Answers read, and how many named / cited the shop. Failed calls are not answers. */
export function visibilityCounts(rows: VisibilityRow[] = []): { answers: number; named: number; cited: number; failed: number } {
    const answered = rows.filter((r) => !r.error);
    return {
        answers: answered.length,
        named: answered.filter((r) => r.mentioned).length,
        cited: answered.filter((r) => r.cited).length,
        failed: rows.length - answered.length,
    };
}

/** Who the assistants name, most often first: the shops the vendor is compared with. */
export function competitorTally(rows: VisibilityRow[] = [], max = 8): Array<{ name: string; count: number }> {
    const counts = new Map<string, number>();
    for (const row of rows) {
        if (row.error) continue;
        // Once per answer, however often the answer repeats a name.
        for (const name of new Set(row.competitors)) counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    return [...counts.entries()]
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
        .slice(0, max);
}

/** The sites the assistants read, most cited first. */
export function sourceTally(rows: VisibilityRow[] = [], max = 8): Array<{ domain: string; count: number }> {
    const counts = new Map<string, number>();
    for (const row of rows) {
        if (row.error) continue;
        for (const domain of new Set(row.sources.map((s) => s.domain))) counts.set(domain, (counts.get(domain) ?? 0) + 1);
    }
    return [...counts.entries()]
        .map(([domain, count]) => ({ domain, count }))
        .sort((a, b) => b.count - a.count || a.domain.localeCompare(b.domain))
        .slice(0, max);
}

/** The score over time, one point per finished check. */
export function scoreTrend(history: VisibilityHistoryPoint[] = []): TrendPoint[] {
    return history
        .filter((h) => typeof h.score === 'number')
        .map((h) => ({ label: h.at.slice(0, 10), value: h.score as number }));
}

/** How far a running check has got: answers in / answers planned. */
export function runProgress(check: VisibilityCheck | null): { done: number; total: number } {
    if (!check) return { done: 0, total: 0 };
    return { done: check.results.length, total: check.prompts.length * Math.max(1, check.engines.length) };
}

/** In words: none (0), low (<20), some (<50), strong. Null before any check. */
export function scoreBand(score: number | null | undefined): 'none' | 'low' | 'some' | 'strong' | null {
    if (score == null) return null;
    if (score <= 0) return 'none';
    if (score < 20) return 'low';
    if (score < 50) return 'some';
    return 'strong';
}

/** "$0.09" — what a check cost, never rounded up to look cheaper. */
export const formatUsd = (value: number | null | undefined): string =>
    value == null ? '—' : value < 0.01 && value > 0 ? `$${value.toFixed(4)}` : `$${value.toFixed(2)}`;

/** The rows a table shows: grouped by question, in the order the questions were asked. */
export function rowsByPrompt(check: VisibilityCheck | null): VisibilityRow[] {
    if (!check) return [];
    const order = new Map(check.prompts.map((p, i) => [p.id, i]));
    const engineOrder = new Map(check.engines.map((e, i) => [e.id, i]));
    return [...check.results].sort(
        (a, b) => (order.get(a.promptId) ?? 99) - (order.get(b.promptId) ?? 99) || (engineOrder.get(a.engine) ?? 9) - (engineOrder.get(b.engine) ?? 9),
    );
}
